import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { dedupById } from "@/lib/utils";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlusIcon, TrashIcon, DownloadSimpleIcon, GaugeIcon, SparkleIcon, CheckCircleIcon, WarningCircleIcon, ArrowsClockwiseIcon, PencilIcon, EyeIcon, LockIcon, ClockCounterClockwiseIcon } from "@phosphor-icons/react";
import { exportToCSV } from "@/lib/csv";
import { useAuth } from "@/lib/auth";
import PlanningCalculator from "./production/PlanningCalculator";
import GanttTab from "./production/GanttTab";
import PlanVsActualTable from "./production/PlanVsActualTable";
import { RowActions } from "@/components/RowActions";

import FormattedNumberInput from "@/components/FormattedNumberInput";
const inp = "w-full h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-sm focus:outline-none focus:border-[var(--accent)]";
const selCls = "bg-[var(--bg-0)] border-[var(--border)] text-white h-9";

const TABS = [
  { key: "plans", label: "Plans · Planned vs Actual" },
  { key: "capacity", label: "Capacity Planning" },
  { key: "gantt", label: "Gantt Board" },
  { key: "delivery", label: "Estimated Delivery" },
  { key: "allocate", label: "Auto Loom Allocation" },
];

// P17-UAT WO-PLANNING-DOC-IDENTITY · deterministic sort shared by Plan vs Actual
// and Estimated Delivery: latest Plan Date first, then Plan Doc No. DESC; legacy
// records (no plan_date) always sink below dated/numbered documents.
const planDocCompare = (a, b) => {
  const ad = a.plan_date || "", bd = b.plan_date || "";
  if (ad && !bd) return -1;
  if (!ad && bd) return 1;
  if (ad !== bd) return ad < bd ? 1 : -1;
  const an = a.plan_no || "", bn = b.plan_no || "";
  return an < bn ? 1 : an > bn ? -1 : 0;
};

export default function ProductionPlan() {
  const { hasPerm } = useAuth();
  const [tab, setTab] = useState("plans");
  const [orders, setOrders] = useState([]);
  const [items, setItems] = useState([]);
  const [machines, setMachines] = useState([]);
  const [operators, setOperators] = useState([]);
  const [pva, setPva] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [esoOrders, setEsoOrders] = useState([]);

  const loadCommon = async () => {
    const [o, it, m, op, pv, eso, wo] = await Promise.all([
      api.get("/sales-orders/"),
      api.get("/items/"),
      api.get("/machines/"),
      api.get("/operators/"),
      api.get("/dashboard/planned-vs-actual"),
      // Feb 2026 · pull Export SOs and normalise them onto the SO shape so
      // the Gantt board, Capacity Planner, Allocate tab and Estimated
      // Delivery per SO + Product table can all resolve `order_id` back to
      // a human-readable ESO number + promised delivery date.
      api.get("/exports/orders/").catch(() => ({ data: [] })),
      // Feb 2026 · fetch Work Orders so the New Plan dialog can bi-directionally
      // pair SO/ESO ↔ WO like the Planning Calculator does.
      api.get("/work-orders/").catch(() => ({ data: [] })),
    ]);
    const esoMapped = (eso.data || []).map(e => ({
      id: e.id,
      order_no: e.doc_no,
      customer_name: e.buyer_name,
      customer: e.buyer_name,
      po_number: e.buyer_po_no,
      order_date: e.order_date,
      delivery_date: e.delivery_date,
      items: (e.items || []).map(li => ({
        article_no: li.article_no,
        item_id: li.item_id,
        order_qty: +(li.qty || 0),
        qty: +(li.qty || 0),
        item_no: li.item_no,
        style_no: li.style_no,
        hs_code: li.hs_code,
        delivery_date: li.delivery_date || e.delivery_date,
      })),
      _is_export: true,
    }));
    setOrders(dedupById([...(o.data || []), ...esoMapped]));
    setEsoOrders(esoMapped);
    setWorkOrders((wo.data || []).filter(p => p.status === "Approved"));
    setItems(it.data); setMachines(m.data); setOperators(op.data); setPva(pv.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadCommon(); }, []);

  return (
    <div className="p-6 space-y-5">
      {/* Sep 03, 2026 · When the Gantt / Delivery tab is active, collapse
          the wordy page header onto one line so the board claims its
          full 80vh grid without the description eating vertical space. */}
      {(tab === "gantt" || tab === "delivery" || tab === "capacity") ? (
        <div className="flex flex-wrap items-center gap-3 border-b border-[var(--border)]">
          <div className="overline text-zinc-500">Scheduling</div>
          <div className="text-zinc-600">/</div>
          <h1 className="font-display text-base font-bold tracking-tight text-white">Production Planning</h1>
          <span className="text-[10px] text-zinc-500 hidden lg:inline">SO → Capacity → Loom → Live</span>
          <div className="ml-auto flex flex-wrap gap-1">
            {TABS.map(t => (
              <button
                key={t.key}
                data-testid={`planning-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`px-3 py-1.5 text-[11px] font-medium uppercase tracking-widest border-b-2 transition-colors ${tab === t.key ? "border-[var(--accent)] text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      ) : (<>
      <div className="flex items-end justify-between">
        <div>
          <div className="overline mb-1">Scheduling</div>
          <h1 className="font-display text-3xl font-black tracking-tight text-white">Production Planning</h1>
          <p className="text-xs text-zinc-500 mt-1">Closed-loop planning · Sales Order → Capacity → Loom Allocation → Live Monitoring</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-[var(--border)]">
        {TABS.map(t => (
          <button
            key={t.key}
            data-testid={`planning-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2.5 text-xs font-medium uppercase tracking-widest border-b-2 transition-colors ${tab === t.key ? "border-[var(--accent)] text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      </>)}

      {tab === "plans" && <PlansTab pva={pva} orders={orders} esoOrders={esoOrders} workOrders={workOrders} items={items} machines={machines} operators={operators} refresh={loadCommon} />}
      {tab === "capacity" && <CapacityTab orders={orders} items={items} />}
      {tab === "gantt" && <GanttTab machines={machines} orders={orders} items={items} />}
      {tab === "delivery" && <GanttTab machines={machines} orders={orders} items={items} deliveryOnly />}
      {tab === "allocate" && <AllocateTab orders={orders} esoOrders={esoOrders} workOrders={workOrders} items={items} onCreatePlan={loadCommon} />}
    </div>
  );
}

// ================================================================
// TAB 1 — Plans (planned vs actual)
// ================================================================
function PlansTab({ pva, orders, esoOrders = [], workOrders = [], items, machines, operators, refresh }) {
  const { hasPerm } = useAuth();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [amending, setAmending] = useState(null); // full plan row + current amendments
  // Feb 2026 · Dual-picker state (mirrors PlanningCalculator behaviour). The
  // planner toggles between SO and ESO source pools, and picking a WO auto-
  // syncs the SO/ESO picker via `source_order_id` (bi-directional).
  const [sourceKind, setSourceKind] = useState("domestic"); // "domestic" | "export"
  const [selectedPoId, setSelectedPoId] = useState("");
  // Feb 2026 · Daily Target auto-calc (mirrors Planning Calculator formula):
  //   Effective m/hr per head = (speed_rpm / picks_per_cm) × 60/100 × efficiency_pct/100
  //   Daily Target           = m/hr per head × heads × hours/day
  // Values come from the picked Item's Product-Master fields. Users can still
  // override the Daily Target field manually — the auto-push only triggers
  // when the current value is 0 or still equals the last computed value.
  const [heads, setHeads] = useState(1);
  const [hoursPerDay, setHoursPerDay] = useState(22);
  const empty = {
    order_id: "", item_id: "", machine_id: "", operator_id: "",
    planned_qty: 5000, planned_start: new Date().toISOString().slice(0, 10),
    planned_finish: new Date(Date.now() + 5 * 86400000).toISOString().slice(0, 10),
    daily_target: 1000, status: "Planned",
  };
  const [form, setForm] = useState(empty);

  // Domestic SOs, ESOs, and Stock orders. Stock pool = any SO/ESO tagged
  // `order_against === "stock"` (Feb 2026 · Order-Against tab). Feb 2026 ·
  // planner request: STOCK is now a primary source alongside SO/ESO; Work
  // Order is a secondary reference that auto-syncs.
  const domesticOrders = useMemo(() => orders.filter(o => !o._is_export && o.order_against !== "stock"), [orders]);
  const stockOrders = useMemo(() => [
    ...orders.filter(o => !o._is_export && o.order_against === "stock"),
    ...esoOrders.filter(o => o.order_against === "stock"),
  ], [orders, esoOrders]);
  const nonStockEso = useMemo(() => esoOrders.filter(o => o.order_against !== "stock"), [esoOrders]);
  const visibleOrders =
    sourceKind === "export" ? nonStockEso :
    sourceKind === "stock"  ? stockOrders :
    domesticOrders;
  const sourceLabel = { domestic: "Sales Order", export: "ESO", stock: "Stock Order" }[sourceKind];

  // Feb 2026 · Derived Daily Target from the picked Item + heads + hours/day
  // (same formula the Planning Calculator uses at line 176-186).
  const matchedItem = useMemo(() => items.find(i => i.id === form.item_id) || null, [items, form.item_id]);
  const _picksPerCm = +(matchedItem?.standard_picks_per_cm ?? matchedItem?.picks_per_cm ?? 0);
  const _speedRpm   = +(matchedItem?.standard_speed_rpm ?? 0);
  const _stdEff     = +(matchedItem?.standard_efficiency_pct ?? 0);
  const computedDailyTarget = useMemo(() => {
    if (!_picksPerCm || !_speedRpm) return 0;
    const perHrPerHead = (_speedRpm / _picksPerCm) * (60 / 100);
    const effPerHrPerHead = perHrPerHead * ((_stdEff / 100) || 1);
    return Math.round(effPerHrPerHead * (+heads || 0) * (+hoursPerDay || 0));
  }, [_picksPerCm, _speedRpm, _stdEff, heads, hoursPerDay]);
  // Track the last value we auto-pushed so we don't clobber a manual edit.
  const lastPushedRef = React.useRef(0);
  useEffect(() => {
    if (!computedDailyTarget) return;
    setForm(f => {
      const current = +f.daily_target || 0;
      const isDefault = current === 1000; // seeded default in `empty`
      const wasAutoPushed = current === lastPushedRef.current;
      if (current === 0 || isDefault || wasAutoPushed) {
        lastPushedRef.current = computedDailyTarget;
        return { ...f, daily_target: computedDailyTarget };
      }
      return f;
    });
  }, [computedDailyTarget]);

  // When a WO is picked, auto-fill the SO/ESO/Stock side + item.
  useEffect(() => {
    if (!selectedPoId) return;
    const wo = workOrders.find(p => p.id === selectedPoId);
    if (!wo) return;
    // Feb 2026 · Resolve the WO's linked source into one of three kinds:
    //   1. Its linked order is order_against='stock' → "stock"
    //   2. source_type === 'ESO' → "export"
    //   3. everything else → "domestic"
    const linked = [...orders, ...esoOrders].find(o => o.id === wo.source_order_id);
    if (linked?.order_against === "stock") setSourceKind("stock");
    else if (wo.source_type === "ESO") setSourceKind("export");
    else setSourceKind("domestic");
    // Only patch if different — avoids a render loop with the reverse-sync effect.
    setForm(f => {
      const next = { ...f };
      if (wo.source_order_id && next.order_id !== wo.source_order_id) next.order_id = wo.source_order_id;
      if (wo.item_id && !next.item_id) next.item_id = wo.item_id;
      if (+wo.production_qty > 0 && (!next.planned_qty || next.planned_qty === 5000)) next.planned_qty = +wo.production_qty;
      return next;
    });
  }, [selectedPoId, workOrders]);

  // When the SO/ESO is picked directly, auto-select the linked WO (if any).
  useEffect(() => {
    if (!form.order_id) return;
    const currentWo = workOrders.find(p => p.id === selectedPoId);
    if (currentWo && currentWo.source_order_id === form.order_id) return;
    const wo = workOrders.find(p => p.source_order_id === form.order_id);
    setSelectedPoId(wo ? wo.id : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.order_id, workOrders]);

  const openNew = () => {
    setEditing(null);
    setForm(empty);
    setSelectedPoId("");
    setSourceKind("domestic");
    setOpen(true);
  };
  const openEdit = async (row) => {
    try {
      const { data } = await api.get(`/production-plans/${row.plan_id}`);
      setEditing(row.plan_id);
      setForm({
        order_id: data.order_id || "",
        item_id: data.item_id || "",
        machine_id: data.machine_id || "",
        operator_id: data.operator_id || "",
        planned_qty: data.planned_qty ?? 0,
        planned_start: data.planned_start || new Date().toISOString().slice(0, 10),
        planned_finish: data.planned_finish || new Date().toISOString().slice(0, 10),
        daily_target: data.daily_target ?? 0,
        status: data.status || "Planned",
      });
      // Detect whether this plan's order_id belongs to the Export pool so the
      // dialog opens on the correct source tab.
      setSourceKind(esoOrders.some(e => e.id === data.order_id) ? "export" : "domestic");
      const wo = workOrders.find(p => p.source_order_id === data.order_id);
      setSelectedPoId(wo ? wo.id : "");
      setOpen(true);
    } catch (e) { toast.error("Failed to load plan"); }
  };
  const openAmend = async (row) => {
    try {
      const { data } = await api.get(`/production-plans/${row.plan_id}`);
      setAmending({ ...data, plan_id: row.plan_id, order_no: row.order_no, customer: row.customer, actual_qty: row.actual_qty });
    } catch (e) { toast.error("Failed to load plan"); }
  };

  const save = async () => {
    try {
      if (editing) {
        await api.put(`/production-plans/${editing}`, form);
        toast.success("Plan updated");
      } else {
        await api.post("/production-plans/", form);
        // Sep 04, 2026 · Auto-link Missing WO nudge. If this new plan
        // has no linked Work Order (no WO shares its source_order_id +
        // item_id), the schedule falls back to SO order_qty as its cap
        // — which is exactly what the WO-Qty-source-of-truth rule is
        // designed to prevent. Toast the planner with a Create WO
        // quick-action so the schedule always locks to a WO cap.
        const hasLinkedWO = workOrders.some(wo =>
          (wo.source_order_id || "") === form.order_id &&
          (!form.item_id || wo.item_id === form.item_id ||
            (Array.isArray(wo.items) && wo.items.some(it => it && it.item_id === form.item_id)))
        );
        if (!hasLinkedWO) {
          toast.warning("Plan saved — but no Work Order links this SO yet. Schedule cap is falling back to SO Order Qty.", {
            action: {
              label: "Create WO",
              onClick: () => window.open(`/production/orders?prefill_so=${encodeURIComponent(form.order_id)}${form.item_id ? "&prefill_item=" + encodeURIComponent(form.item_id) : ""}`, "_self"),
            },
            duration: 8000,
          });
        } else {
          toast.success("Plan created");
        }
      }
      setOpen(false); setEditing(null); setForm(empty); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const remove = async (id) => {
    if (!confirm("Delete this plan?")) return;
    await api.delete(`/production-plans/${id}`);
    toast.success("Deleted"); refresh();
  };
  // Feb 2026 · Universal Release — one-click unallocate that clears the
  // machine reservation regardless of Locked/Unlocked. Backend hard-blocks
  // if any Production Entry has been posted; the toast surfaces the exact
  // detail string ("Please use Short Close instead.") so operators don't
  // have to guess. Restricted server-side to Admin / Production Manager.
  const releaseToQueue = async (row) => {
    const label = row.machine_code || "machine";
    if (!confirm(`Release ${row.order_no || "this plan"} from ${label} and return the WO to the unallocated queue?`)) return;
    try {
      const { data } = await api.post(`/production-plans/${row.plan_id}/release`);
      toast.success(`Released ${Number(data.planned_qty || 0).toLocaleString("en-IN")}m from ${label}`);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Release failed"); }
  };

  const planActions = (r, i) => [
    { label: "View", testid: `view-plan-${i}`, onClick: () => setViewing(r) },
    { label: "Edit", testid: `edit-plan-${i}`, show: (Number(r.actual_qty) || 0) === 0, onClick: () => openEdit(r) },
    { label: "Amend", tone: "warn", testid: `amend-plan-${i}`, show: (Number(r.actual_qty) || 0) > 0, onClick: () => openAmend(r) },
    { label: "Release to Queue", tone: "warn", testid: `release-plan-${i}`, onClick: () => releaseToQueue(r) },
    { separator: true },
    { label: "Delete", tone: "danger", testid: `delete-plan-${i}`, onClick: () => remove(r.plan_id) },
  ];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs text-zinc-500" data-testid="pva-plan-count">{pva.length} machine-plan allocation{pva.length === 1 ? "" : "s"}</div>
        <button data-testid="new-plan-btn" onClick={openNew} className={`h-9 px-4 bg-[var(--accent)] hover:bg-blue-600 text-white text-sm font-semibold rounded-sm flex items-center gap-2 ${hasPerm("planning:edit") ? "" : "hidden"}`}>
          <PlusIcon size={14} weight="bold" />New Plan
        </button>
      </div>

      <PlanVsActualTable rows={pva} machines={machines} actions={planActions} />

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(empty); setSelectedPoId(""); setSourceKind("domestic"); } }}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-3xl" description="Production plan action — set machine, quantities and schedule for the selected order.">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Production Plan" : "New Production Plan"}</DialogTitle></DialogHeader>
          {/* Dual-picker (Feb 2026 · mirrors PlanningCalculator) — Source
              (SO / ESO / STOCK) is the PRIMARY pickup; Work Order is a
              SECONDARY reference and syncs bi-directionally. Planner request
              Feb 2026: STOCK added as a first-class source alongside SO+ESO. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
            <div>
              <div className="overline mb-1.5 flex items-center justify-between">
                <span>Source · Primary</span>
                <div className="inline-flex rounded-sm border border-[var(--border)] overflow-hidden text-[9px]">
                  {[
                    { k: "domestic", label: "SO",    testid: "plan-src-domestic" },
                    { k: "export",   label: "ESO",   testid: "plan-src-export" },
                    { k: "stock",    label: "STOCK", testid: "plan-src-stock" },
                  ].map(t => (
                    <button
                      key={t.k}
                      type="button"
                      data-testid={t.testid}
                      onClick={() => { setSourceKind(t.k); setForm(f => ({ ...f, order_id: "" })); setSelectedPoId(""); }}
                      className={`px-2 py-1 uppercase tracking-widest transition ${sourceKind === t.k ? "bg-[var(--accent)] text-white" : "text-zinc-400 hover:bg-[var(--bg-2)]"}`}
                    >{t.label}</button>
                  ))}
                </div>
              </div>
              <Select value={form.order_id} onValueChange={v => setForm({ ...form, order_id: v })}>
                <SelectTrigger data-testid="plan-order" className={selCls}>
                  <SelectValue placeholder={visibleOrders.length ? `Pick a ${sourceLabel}…` : `No ${sourceLabel}s available`} />
                </SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-72">
                  {visibleOrders.length === 0 && (
                    <div className="px-3 py-4 text-xs text-zinc-500 italic">
                      No {sourceLabel}s available.
                    </div>
                  )}
                  {visibleOrders.map(o => {
                    const kindBadge = sourceKind === "stock"
                      ? { label: o._is_export ? "STOCK-ESO" : "STOCK-SO", cls: "bg-emerald-500/15 text-emerald-300" }
                      : { label: o._is_export ? "ESO" : "SO",             cls: "bg-blue-500/15 text-blue-300" };
                    return (
                      <SelectItem key={o.id} value={o.id}>
                        <span className={`mr-1.5 px-1 py-0.5 rounded-sm ${kindBadge.cls} text-[9px] font-mono uppercase tracking-widest`}>{kindBadge.label}</span>
                        {o.order_no}
{/* customer name hidden on production plan · Mar 2026 */}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {form.order_id && (
                <div data-testid="plan-order-corr-chip" className="mt-1 text-[10px] text-zinc-500 font-mono">
                  {(() => {
                    const wo = workOrders.find(p => p.source_order_id === form.order_id);
                    return wo
                      ? <>Linked Work Order: <span className="text-emerald-300">{wo.po_no}</span> · <span className="text-zinc-400">{wo.status}</span></>
                      : <span className="text-amber-400">No approved Work Order yet — you can still plan directly.</span>;
                  })()}
                </div>
              )}
            </div>
            <div>
              <div className="overline mb-1.5">Work Order · Secondary Ref</div>
              <Select value={selectedPoId} onValueChange={setSelectedPoId}>
                <SelectTrigger data-testid="plan-wo-select" className={selCls}>
                  <SelectValue placeholder={workOrders.length ? "Pick a Work Order…" : "No approved WOs"} />
                </SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-72">
                  {workOrders.length === 0 && (
                    <div className="px-3 py-4 text-xs text-zinc-500 italic">
                      No approved Work Orders yet — approve one in the Work Orders module first.
                    </div>
                  )}
                  {workOrders.map(po => (
                    <SelectItem key={po.id} value={po.id}>
                      <span className="mr-1.5 px-1 py-0.5 rounded-sm bg-emerald-500/15 text-emerald-300 text-[9px] font-mono uppercase tracking-widest">{po.source_type}</span>
                      {po.po_no}
{/* customer name hidden on production plan · Mar 2026 */}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedPoId && (
                <div data-testid="plan-wo-source-chip" className="mt-1 text-[10px] text-zinc-500 font-mono">
                  Linked Source: <span className="text-blue-300">{workOrders.find(p => p.id === selectedPoId)?.source_order_no || "—"}</span>
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Item">
              <Select value={form.item_id} onValueChange={v => setForm({ ...form, item_id: v })}>
                <SelectTrigger data-testid="plan-item" className={selCls}><SelectValue placeholder="Select item" /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
                  {items.map(i => <SelectItem key={i.id} value={i.id}>{i.code} — {i.description}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Machine">
              <Select value={form.machine_id} onValueChange={v => setForm({ ...form, machine_id: v })}>
                <SelectTrigger data-testid="plan-machine" className={selCls}><SelectValue placeholder="Select machine" /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
                  {machines.map(m => <SelectItem key={m.id} value={m.id}>{m.code} · {m.status}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Operator">
              <Select value={form.operator_id} onValueChange={v => setForm({ ...form, operator_id: v })}>
                <SelectTrigger className={selCls}><SelectValue placeholder="Select operator" /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                  {operators.map(o => <SelectItem key={o.id} value={o.id}>{o.name} ({o.code})</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Planned Qty (m)">
              <FormattedNumberInput data-testid="plan-qty" value={form.planned_qty} onChange={e => setForm({ ...form, planned_qty: parseFloat(e.target.value) || 0 })} className={inp} />
              <PlanBalanceHint
                orderId={form.order_id}
                itemId={form.item_id}
                excludePlanId={editing || ""}
                plannedQty={+form.planned_qty || 0}
              />
            </F>
            <F label={<>Daily Target (m) <span className="text-[9px] text-[var(--accent)] font-mono uppercase tracking-widest ml-1">auto</span></>}>
              <FormattedNumberInput data-testid="plan-daily-target" value={form.daily_target} onChange={e => { const v = parseFloat(e.target.value) || 0; lastPushedRef.current = 0; setForm({ ...form, daily_target: v }); }} className={inp} />
              {computedDailyTarget > 0 && (
                <div data-testid="plan-daily-target-hint" className="mt-1 text-[10px] font-mono text-zinc-500 leading-tight">
                  = ({_speedRpm} / {_picksPerCm}) × 60/100 × {_stdEff || 100}% × {heads}h × {hoursPerDay}hr
                  <span className="ml-1 text-emerald-300">→ {computedDailyTarget} m/day</span>
                  {(+form.daily_target || 0) !== computedDailyTarget && (
                    <button
                      type="button"
                      data-testid="plan-daily-target-recalc"
                      onClick={() => { lastPushedRef.current = computedDailyTarget; setForm(f => ({ ...f, daily_target: computedDailyTarget })); }}
                      className="ml-2 px-1.5 py-0.5 rounded-sm border border-[var(--accent)]/40 text-[var(--accent)] hover:bg-[var(--accent)]/10 uppercase tracking-widest"
                    >Recalc</button>
                  )}
                </div>
              )}
              {!matchedItem && form.item_id && (
                <div className="mt-1 text-[10px] text-amber-400">Pick an item to auto-compute the daily target.</div>
              )}
              {matchedItem && (!_picksPerCm || !_speedRpm) && (
                <div className="mt-1 text-[10px] text-amber-400">Item missing picks/cm or speed — set them in Product Master to auto-compute.</div>
              )}
            </F>
            <F label="Heads / Machine"><FormattedNumberInput data-testid="plan-heads" step="1" min="1" value={heads} onChange={e => setHeads(parseFloat(e.target.value) || 0)} className={inp} /></F>
            <F label="Hours / Day"><FormattedNumberInput data-testid="plan-hours" step="1" min="1" max="24" value={hoursPerDay} onChange={e => setHoursPerDay(parseFloat(e.target.value) || 0)} className={inp} /></F>
            <F label="Planned Start"><input type="date" value={form.planned_start} onChange={e => setForm({ ...form, planned_start: e.target.value })} className={inp} /></F>
            <F label="Planned Finish"><input type="date" value={form.planned_finish} onChange={e => setForm({ ...form, planned_finish: e.target.value })} className={inp} /></F>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400">Cancel</button>
            <button data-testid="save-plan" onClick={save} className="px-4 py-2 rounded-sm bg-[var(--accent)] hover:bg-blue-600 text-sm font-semibold text-white">{editing ? "Update Plan" : "Save"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Read-only view dialog */}
      <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-3xl" data-testid="plan-view-dialog" description="Production plan action — set machine, quantities and schedule for the selected order.">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-3">
              <span>Production Plan · {viewing?.order_no}</span>
              <StatusBadge status={viewing?.plan_status} />
              {(Number(viewing?.actual_qty) || 0) > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-mono bg-amber-500/15 text-amber-400"><LockIcon size={10} weight="bold" />PRODUCTION STARTED</span>
              )}
            </DialogTitle>
          </DialogHeader>
          {viewing && (
            <div className="space-y-4">
              <div className="border border-[var(--border)] rounded-sm bg-[var(--bg-2)]/60 px-4 py-3 grid grid-cols-4 gap-3 text-xs">
{/* Customer hidden on production floor · Mar 2026 */}
                <div><div className="overline text-[10px] mb-0.5">Item</div><div className="font-mono">{viewing.item_code || "—"}</div></div>
                <div><div className="overline text-[10px] mb-0.5">Machine</div><div className="font-mono">{viewing.machine_code || "—"}</div></div>
                <div><div className="overline text-[10px] mb-0.5">Operator</div><div className="font-mono">{viewing.operator_name || viewing.operator || "—"}</div></div>
              </div>

              <div className="grid grid-cols-4 gap-3 text-xs">
                <div className="tactical-card p-3"><div className="overline text-[9px] mb-1">Planned Qty</div><div className="font-mono text-lg font-semibold text-white">{Number(viewing.planned_qty).toFixed(0)}<span className="text-[10px] text-zinc-500 ml-1">m</span></div></div>
                <div className="tactical-card p-3"><div className="overline text-[9px] mb-1">Actual</div><div className={`font-mono text-lg font-semibold ${(Number(viewing.actual_qty)||0) > 0 ? "text-emerald-400" : "text-zinc-500"}`}>{Number(viewing.actual_qty).toFixed(0)}<span className="text-[10px] text-zinc-500 ml-1">m</span></div></div>
                <div className="tactical-card p-3"><div className="overline text-[9px] mb-1">Variance</div><div className={`font-mono text-lg font-semibold ${viewing.status === "green" ? "text-emerald-400" : viewing.status === "yellow" ? "text-amber-400" : "text-red-400"}`}>{viewing.variance_pct}%</div></div>
                <div className="tactical-card p-3"><div className="overline text-[9px] mb-1">Progress</div><div className="font-mono text-lg font-semibold text-white">{viewing.completion_pct}%</div></div>
              </div>

              <div className="grid grid-cols-4 gap-3 text-xs">
                <div><div className="overline text-[10px] mb-0.5">Planned Start</div><div className="font-mono">{viewing.planned_start || "—"}</div></div>
                <div><div className="overline text-[10px] mb-0.5">Planned Finish</div><div className="font-mono">{viewing.planned_finish || "—"}</div></div>
                <div><div className="overline text-[10px] mb-0.5">Committed Delivery</div><div className="font-mono">{viewing.committed_delivery || "—"}</div></div>
                <div><div className="overline text-[10px] mb-0.5">Forecast Finish</div><div className={`font-mono ${viewing.status === "red" ? "text-red-400" : ""}`}>{viewing.forecast_finish || "—"}</div></div>
                <div><div className="overline text-[10px] mb-0.5">Daily Target</div><div className="font-mono">{viewing.daily_target || "—"}<span className="text-[10px] text-zinc-500 ml-1">m/day</span></div></div>
                <div><div className="overline text-[10px] mb-0.5">Plan Status</div><div><StatusBadge status={viewing.plan_status} /></div></div>
              </div>
            </div>
          )}
          <DialogFooter>
            {viewing && (Number(viewing.actual_qty) || 0) === 0 && (
              <button
                data-testid="plan-view-edit"
                onClick={() => { const r = viewing; setViewing(null); openEdit(r); }}
                className="px-4 py-2 rounded-sm border border-[var(--accent)] text-[var(--accent)] text-sm font-semibold flex items-center gap-2"
              >
                <PencilIcon size={14} weight="bold" />Edit
              </button>
            )}
            <button onClick={() => setViewing(null)} className="px-4 py-2 rounded-sm bg-[var(--accent)] text-sm font-semibold text-white">Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ================================================================
// TAB 2 — Capacity Planning (sub-tabs: Calendar heatmap + Planning Calculator)
// ================================================================
function CapacityTab({ orders = [], items = [] }) {
  const [sub, setSub] = useState("calendar");
  return (
    <div className="space-y-2">
      {/* Sep 03, 2026 · Slim capacity header — title + sub-tabs share one
          tight row so the calendar grid claims the largest vertical share.
          Full-width `flex` keeps the tabs pushed to the right edge for a
          familiar Chrome-style layout. */}
      <div className="flex flex-wrap items-center gap-3 border-b border-[var(--border)]/60">
        <span className="overline text-zinc-500">Capacity</span>
        <span className="text-zinc-600">/</span>
        <span className="font-mono uppercase tracking-widest text-xs text-white">Planning</span>
        <span className="text-[10px] text-zinc-500 hidden lg:inline">Loom load · daily meters · utilization</span>
        <div className="ml-auto flex flex-wrap gap-1">
          {[
            { key: "calendar", label: "Capacity Calendar" },
            { key: "heatmap", label: "Production Heatmap" },
            { key: "calculator", label: "Planning Calculator" },
          ].map(t => (
            <button
              key={t.key}
              data-testid={`cap-sub-${t.key}`}
              onClick={() => setSub(t.key)}
              className={`px-3 py-1.5 text-[11px] font-medium uppercase tracking-widest border-b-2 transition-colors ${sub === t.key ? "border-[var(--accent)] text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      {sub === "calendar" && <CapacityCalendar />}
      {sub === "heatmap" && <ProductionHeatmap />}
      {sub === "calculator" && <PlanningCalculator orders={orders} items={items} />}
    </div>
  );
}

// ---------- Production Heatmap ----------
// Loom × day grid coloured by daily `actual / daily_target`. Emerald ≥ 90 %, amber
// 70–90 %, red < 70 %, zinc = no plan running on that loom that day. Aggregates
// production_entries client-side against production_plans' `daily_target`.
function ProductionHeatmap() {
  const today = new Date().toISOString().slice(0, 10);
  const from14 = new Date(Date.now() - 13 * 86400000).toISOString().slice(0, 10);
  const [from, setFrom] = useState(from14);
  const [to, setTo] = useState(today);
  const [machines, setMachines] = useState([]);
  const [entries, setEntries] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/machines/").then(r => r.data),
      api.get("/production-entries/").then(r => r.data),
      api.get("/production-plans/").then(r => r.data),
    ]).then(([m, e, p]) => {
      setMachines(m); setEntries(e); setPlans(p); setLoading(false);
    }).catch(() => setLoading(false));
  };
  useEffect(load, []);

  // Build the day list [from … to] inclusive.
  const days = useMemo(() => {
    if (!from || !to) return [];
    const out = []; const start = new Date(from); const end = new Date(to);
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      out.push(d.toISOString().slice(0, 10));
    }
    return out;
  }, [from, to]);

  // (machineId, entry_date) → summed produced meters (only entries in the range).
  const actualByCell = useMemo(() => {
    const acc = {};
    for (const e of entries) {
      if (e.entry_date < from || e.entry_date > to) continue;
      const key = `${e.machine_id}|${e.entry_date}`;
      acc[key] = (acc[key] || 0) + (+e.produced_meter || 0);
    }
    return acc;
  }, [entries, from, to]);

  // (machineId, day) → daily_target of the plan whose window covers that day.
  const targetForCell = (mid, day) => {
    const p = plans.find(pl =>
      pl.machine_id === mid &&
      pl.planned_start <= day && pl.planned_finish >= day &&
      ["Planned", "Running", "Paused", "Completed"].includes(pl.status)
    );
    return p ? +(p.daily_target || 0) : 0;
  };

  const cellClass = (actual, target) => {
    if (!target) return actual > 0 ? "bg-blue-500/30 text-blue-100" : "bg-transparent text-zinc-600";
    const pct = actual / target;
    if (pct >= 0.9) return "bg-emerald-500/60 text-white";
    if (pct >= 0.7) return "bg-amber-500/50 text-white";
    if (pct > 0)    return "bg-red-500/60 text-white";
    return "bg-zinc-700/40 text-zinc-500"; // planned but no actual yet
  };

  // Show only machines that have activity (plans or entries) in the window.
  const activeMachineIds = useMemo(() => {
    const s = new Set();
    for (const e of entries) if (e.entry_date >= from && e.entry_date <= to) s.add(e.machine_id);
    for (const p of plans) {
      if (p.planned_start <= to && p.planned_finish >= from) s.add(p.machine_id);
    }
    return s;
  }, [entries, plans, from, to]);
  const rows = useMemo(() => machines.filter(m => activeMachineIds.has(m.id)), [machines, activeMachineIds]);

  const totals = useMemo(() => {
    let actual = 0, target = 0;
    for (const m of rows) for (const d of days) {
      actual += actualByCell[`${m.id}|${d}`] || 0;
      target += targetForCell(m.id, d);
    }
    return { actual, target, pct: target > 0 ? (actual / target) * 100 : 0 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, days, actualByCell, plans]);

  return (
    <div className="space-y-4">
      <div className="tactical-card p-4 flex flex-wrap items-center gap-3">
        <GaugeIcon size={20} className="text-emerald-400" weight="bold" />
        <div className="flex-1 min-w-64">
          <div className="overline">Production Heatmap · Actual vs Daily Target</div>
          <div className="text-xs text-zinc-500">Rows = looms with activity in the range · Cells = daily produced meters coloured by target-attainment.</div>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">From</div>
        <input data-testid="heatmap-from" type="date" value={from} onChange={e => setFrom(e.target.value)} className="h-8 bg-[var(--bg-0)] border border-[var(--border)] px-2 rounded-sm text-xs focus:outline-none focus:border-[var(--accent)]" />
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">To</div>
        <input data-testid="heatmap-to" type="date" value={to} onChange={e => setTo(e.target.value)} className="h-8 bg-[var(--bg-0)] border border-[var(--border)] px-2 rounded-sm text-xs focus:outline-none focus:border-[var(--accent)]" />
        <button onClick={load} data-testid="heatmap-refresh" className="h-8 px-3 border border-[var(--border)] hover:bg-[var(--bg-2)] text-xs rounded-sm">Refresh</button>
      </div>

      <div className="flex items-center gap-3 text-[10px] text-zinc-500 px-1 flex-wrap">
        <span>Legend:</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-emerald-500/60"></span>≥ 90 % target</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-amber-500/50"></span>70–90 %</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-red-500/60"></span>&lt; 70 %</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-zinc-700/40"></span>Planned · no output</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-blue-500/30"></span>Output · no plan</span>
        <span className="ml-4 text-zinc-600" data-testid="heatmap-totals">
          Overall {totals.actual.toFixed(0)} / {totals.target.toFixed(0)} m · {totals.pct.toFixed(1)}%
        </span>
      </div>

      <div className="tactical-card overflow-auto max-h-[68vh]">
        <table className="w-full text-[10px] font-mono">
          <thead className="sticky top-0 bg-[var(--bg-1)] z-10">
            <tr className="border-b border-[var(--border)]">
              <th className="text-left px-2 py-2 uppercase tracking-widest text-zinc-500 sticky left-0 bg-[var(--bg-1)] border-r border-[var(--border)] min-w-[9rem]">Loom</th>
              {days.map(d => {
                const dow = new Date(d).getDay();
                const wk = dow === 0 || dow === 6;
                return <th key={d} className={`px-1 py-2 border-r border-[var(--border)]/40 ${wk ? "text-amber-400/60" : "text-zinc-500"}`}>{d.slice(5)}</th>;
              })}
              <th className="px-2 py-2 uppercase tracking-widest text-zinc-500 border-l border-[var(--border)]">Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(m => {
              let rowActual = 0, rowTarget = 0;
              return (
                <tr key={m.id} data-testid={`heatmap-row-${m.code}`} className="border-b border-[var(--border)]/50">
                  <td className="px-2 py-1.5 sticky left-0 bg-[var(--bg-1)] border-r border-[var(--border)]">
                    <div className="text-white font-semibold">{m.code}</div>
                    <div className="text-[9px] text-zinc-500 truncate">{m.machine_type}</div>
                  </td>
                  {days.map(d => {
                    const a = actualByCell[`${m.id}|${d}`] || 0;
                    const t = targetForCell(m.id, d);
                    rowActual += a; rowTarget += t;
                    return (
                      <td key={d} className={`text-center border-r border-[var(--border)]/30 ${cellClass(a, t)}`} title={`${m.code} · ${d}\nActual ${a.toFixed(0)}m / Target ${t.toFixed(0)}m${t > 0 ? ` (${((a / t) * 100).toFixed(0)}%)` : ""}`}>
                        {a > 0 ? a.toFixed(0) : t > 0 ? "·" : ""}
                      </td>
                    );
                  })}
                  <td className="px-2 py-1.5 text-white border-l border-[var(--border)]">
                    <div>{rowActual.toFixed(0)}</div>
                    <div className="text-[9px] text-zinc-500">/ {rowTarget.toFixed(0)}</div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!loading && rows.length === 0 && (
          <div className="text-center py-16 text-zinc-500 text-xs">No production activity in the selected range.</div>
        )}
      </div>
    </div>
  );
}

function CapacityCalendar() {
  const today = new Date().toISOString().slice(0, 10);
  const in14 = new Date(Date.now() + 13 * 86400000).toISOString().slice(0, 10);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(in14);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/planning/capacity?date_from=${from}&date_to=${to}`);
      setData(data);
    } catch (e) { toast.error("Failed to load capacity"); }
    setLoading(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [from, to]);

  const machineTypes = useMemo(() => {
    if (!data) return [];
    const s = new Set(data.machines.map(m => m.machine_type).filter(Boolean));
    return Array.from(s);
  }, [data]);
  const machinesFiltered = useMemo(() => {
    if (!data) return [];
    // Sep 03, 2026 · Ascending alphanumeric sort matches the shop-floor
    // layout — L201 before L202 before L210 — using `localeCompare` with
    // `numeric: true`. Grouped by machine_type first so departments
    // cluster, then sorted within each group.
    const filtered = typeFilter === "all"
      ? data.machines
      : data.machines.filter(m => m.machine_type === typeFilter);
    const groups = new Map();
    for (const m of filtered) {
      const g = m.machine_type || "Uncategorised";
      if (!groups.has(g)) groups.set(g, []);
      groups.get(g).push(m);
    }
    const cmp = (a, b) =>
      String(a.code || "").localeCompare(String(b.code || ""), undefined, { numeric: true, sensitivity: "base" });
    const out = [];
    for (const g of [...groups.keys()].sort((a, b) => a.localeCompare(b))) {
      out.push(...groups.get(g).sort(cmp));
    }
    return out;
  }, [data, typeFilter]);

  const cellColor = (s) => (s === "over" ? "bg-red-500/50" : s === "high" ? "bg-emerald-500/50" : s === "mid" ? "bg-amber-500/40" : s === "low" ? "bg-zinc-600/40" : "bg-transparent");

  return (
    <div className="space-y-2">
      {/* Sep 03, 2026 · Slim control bar — controls on one line so the
          calendar grid keeps its 75vh vertical share. */}
      <div className="tactical-card px-3 py-2 flex flex-wrap items-center gap-2 text-xs">
        <GaugeIcon size={16} className="text-[var(--accent)]" weight="bold" />
        <span className="font-mono uppercase tracking-widest text-zinc-300">Capacity Calendar</span>
        <span className="text-[10px] text-zinc-500 hidden md:inline">Daily loom-meters · cell colour = utilization</span>
        <div className="flex-1 min-w-4" />
        <span className="text-[10px] uppercase tracking-widest text-zinc-500">From</span>
        <input data-testid="cap-from" type="date" value={from} onChange={e => setFrom(e.target.value)} className="h-7 bg-[var(--bg-0)] border border-[var(--border)] px-2 rounded-sm text-xs focus:outline-none focus:border-[var(--accent)]" />
        <span className="text-[10px] uppercase tracking-widest text-zinc-500">To</span>
        <input data-testid="cap-to" type="date" value={to} onChange={e => setTo(e.target.value)} className="h-7 bg-[var(--bg-0)] border border-[var(--border)] px-2 rounded-sm text-xs focus:outline-none focus:border-[var(--accent)]" />
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger data-testid="cap-type-filter" className="bg-[var(--bg-0)] border-[var(--border)] h-7 text-xs w-36"><SelectValue placeholder="All Types" /></SelectTrigger>
          <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
            <SelectItem value="all">All Machine Types</SelectItem>
            {machineTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <button data-testid="cap-refresh" onClick={load} className="h-7 px-2 border border-[var(--border)] hover:bg-[var(--bg-2)] text-xs rounded-sm flex items-center gap-1">
          <ArrowsClockwiseIcon size={11} weight="bold" />Refresh
        </button>
      </div>

      {/* Sep 03, 2026 · KPIs collapsed into a single horizontal strip
          above the grid — replaces the 4-box grid so the calendar sits
          higher on the page. Each stat is separated by a tiny divider
          for scan-ability. */}
      {data && (
        <div className="tactical-card px-3 py-1.5 flex flex-wrap items-center gap-x-5 gap-y-1 text-[11px]" data-testid="cap-kpi-bar">
          <span><span className="text-zinc-500 uppercase tracking-widest text-[9px]">Machines</span> <span className="font-mono text-white ml-1">{data.summary.machine_count}</span></span>
          <span className="text-zinc-700">|</span>
          <span><span className="text-zinc-500 uppercase tracking-widest text-[9px]">Days</span> <span className="font-mono text-white ml-1">{data.summary.days_in_range}</span></span>
          <span className="text-zinc-700">|</span>
          <span><span className="text-zinc-500 uppercase tracking-widest text-[9px]">Planned</span> <span className="font-mono text-amber-400 ml-1">{data.summary.total_planned.toLocaleString()} m</span></span>
          <span className="text-zinc-700">|</span>
          <span><span className="text-zinc-500 uppercase tracking-widest text-[9px]">Utilization</span> <span className={`font-mono ml-1 ${data.summary.overall_utilization_pct > 90 ? "text-red-400" : data.summary.overall_utilization_pct > 60 ? "text-emerald-400" : "text-white"}`}>{data.summary.overall_utilization_pct}%</span></span>
          <div className="ml-auto flex items-center gap-2 text-[10px] text-zinc-500">
            <LegendCell state="low" label="<40%" />
            <LegendCell state="mid" label="40-80%" />
            <LegendCell state="high" label="80-100%" />
            <LegendCell state="over" label=">100%" />
          </div>
        </div>
      )}

      {/* Sep 03, 2026 · Grid claims 75vh so the calendar dominates the
          page. Freeze panes: `<thead>` is `sticky top-0` and the
          Machine + Max/day cells are `sticky left-0` with matching
          background so they stay put during long scrolls.
          Sep 04, 2026 · Slimmed the Machine column to `max-w-[120px]`
          (was min-w-28 / 112px stretched to whatever fit). Machine
          code + type now wrap onto two lines inside that slot. Freed
          horizontal space is redistributed across the date columns
          (each date min-w:44px) so ~30% more days are visible without
          scrolling. Max/day + Date cells are centred for legibility. */}
      <div className="tactical-card overflow-auto h-[75vh]">
        <table className="min-w-full text-xs">
          <thead className="sticky top-0 bg-[var(--bg-1)] z-20">
            <tr className="border-b border-[var(--border)]">
              <th className="text-center px-2 py-1.5 font-medium text-zinc-500 uppercase tracking-widest sticky left-0 bg-[var(--bg-1)] z-30 w-[120px] max-w-[120px]">Machine</th>
              <th className="text-center px-1 py-1.5 font-medium text-zinc-500 uppercase tracking-widest sticky left-[120px] bg-[var(--bg-1)] z-30 w-[68px] max-w-[68px]">Max/day (m)</th>
              {data && data.days.map(d => {
                // Sep 04, 2026 · Non-working shading — the backend now
                // returns a `non_working_days` list containing weekly
                // offs (default Sunday) + registered public / company
                // holidays. We reuse that so the header, footer and
                // Sunday cells all shade consistently.
                const nw = (data.non_working_days || []).includes(d);
                const hname = (data.holiday_names || {})[d] || "";
                const wd = new Date(d + "T00:00:00Z").getUTCDay();
                const label = wd === 0 ? "Sun" : wd === 1 ? "Mon" : wd === 2 ? "Tue" : wd === 3 ? "Wed" : wd === 4 ? "Thu" : wd === 5 ? "Fri" : "Sat";
                return (
                  <th key={d} className={`text-center px-1 py-1.5 font-mono text-[9px] whitespace-nowrap min-w-[44px] ${nw ? "bg-zinc-800/60 text-amber-200 font-semibold" : "text-zinc-500"}`} title={hname ? `${d} · ${hname}` : nw ? `${d} · non-working` : d}>
                    {d.slice(5)}
                    {nw && <div className="text-[8px] text-amber-300/80 leading-none">{hname ? "HOL" : label}</div>}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {machinesFiltered.map(m => (
              <tr key={m.machine_id} className="border-b border-[var(--border)]/60 hover:bg-[var(--bg-2)]/40" data-testid={`cap-row-${m.code}`}>
                <td className="px-2 py-1 sticky left-0 bg-[var(--bg-1)] z-10 w-[120px] max-w-[120px] align-middle">
                  <div className="font-mono font-semibold text-white text-[11px] leading-tight text-center break-words">{m.code}</div>
                  <div className="text-[9px] text-zinc-500 leading-tight text-center whitespace-normal break-words" title={`${m.machine_type} · ${m.status}`}>{m.machine_type}</div>
                </td>
                <td className="px-1 py-1 text-center font-mono text-zinc-400 sticky left-[120px] bg-[var(--bg-1)] z-10 text-[11px] w-[68px] max-w-[68px] align-middle tabular-nums">{m.max_daily_meters}</td>
                {m.cells.map(c => {
                  const nw = c.non_working;
                  const hname = (data.holiday_names || {})[c.date] || "";
                  return (
                    <td key={c.date} className={`px-1 py-1 text-center align-middle min-w-[44px] ${nw ? "bg-zinc-800/40 text-zinc-600" : ""} ${nw ? "" : cellColor(c.state)}`} title={`${c.date}${hname ? " · " + hname : nw ? " · non-working" : ""}\nPlanned: ${c.planned_meters}m / ${c.max_meters}m\nUtil: ${c.utilization_pct}%`}>
                      {nw ? (
                        <div className="font-mono text-[9px] text-amber-300/60 leading-none uppercase tracking-widest">Off</div>
                      ) : (
                        <>
                          <div className="font-mono text-[10px] text-white leading-none tabular-nums">{c.planned_meters > 0 ? Math.round(c.planned_meters) : "·"}</div>
                          {c.utilization_pct > 0 && <div className="font-mono text-[8px] text-zinc-300 leading-none mt-0.5 tabular-nums">{c.utilization_pct}%</div>}
                        </>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
            {!loading && machinesFiltered.length === 0 && (
              <tr><td colSpan={100} className="text-center py-16 text-zinc-500">No machines match the current filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LegendCell({ state, label }) {
  const cls = state === "over" ? "bg-red-500/50" : state === "high" ? "bg-emerald-500/50" : state === "mid" ? "bg-amber-500/40" : "bg-zinc-600/40";
  return <span className="inline-flex items-center gap-1.5"><span className={`inline-block w-3 h-3 rounded-sm ${cls}`}></span>{label}</span>;
}


// ================================================================
// TAB 3 — Auto Loom Allocation
// ================================================================
function AllocateTab({ orders, esoOrders = [], workOrders = [], items, onCreatePlan }) {
  const today = new Date().toISOString().slice(0, 10);
  const [woId, setWoId] = useState("");
  const [woSearch, setWoSearch] = useState("");
  const [qty, setQty] = useState(0);
  const [dueDate, setDueDate] = useState("");
  const [startFrom, setStartFrom] = useState(today);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Feb 2026 · WO-Centric Auto Allocation. A single Work-Order pick
  // populates every downstream field so the planner never has to
  // hand-copy Product Code / Qty / Due Date. `wo`, `sourceOrder`, and
  // `linkedItem` are derived — the SO/ESO number + customer name render
  // as read-only context labels.
  const wo = useMemo(() => workOrders.find(w => w.id === woId), [workOrders, woId]);
  // Feb 2026 · Fix "This WO has no item_id — cannot score looms".
  // Some seeded/legacy WOs (e.g. WO-26-27-00004) have blank item_id at
  // both root and per-line level. Resolve the effective item_id via a
  // ladder: root → items[0].item_id → Item Master lookup by the WO's
  // product_code / article_no.
  const resolvedItemId = useMemo(() => {
    if (!wo) return "";
    if (wo.item_id) return wo.item_id;
    const firstLineItemId = (wo.items || []).map(it => (it || {}).item_id).find(Boolean);
    if (firstLineItemId) return firstLineItemId;
    const pc = wo.item_code
      || (wo.items || []).map(it => (it || {}).item_code || (it || {}).article_no).find(Boolean)
      || "";
    if (!pc) return "";
    const it = items.find(i => i.code === pc || i.article_no === pc || i.description === pc);
    return it ? it.id : "";
  }, [wo, items]);
  const linkedItem = useMemo(
    () => resolvedItemId && items.find(i => i.id === resolvedItemId),
    [resolvedItemId, items],
  );
  const sourceOrder = useMemo(() => {
    if (!wo) return null;
    const src = String(wo.source_type || "SO").toUpperCase();
    const pool = src === "ESO" ? esoOrders : orders;
    return pool.find(o => o.id === wo.source_order_id) || null;
  }, [wo, orders, esoOrders]);

  useEffect(() => {
    if (!wo) return;
    setDueDate(wo.delivery_date || (sourceOrder && sourceOrder.delivery_date) || "");
    // Smart Start — today or the WO's start date if it's later.
    setStartFrom((wo.start_date && wo.start_date > today) ? wo.start_date : today);
    // Feb 2026 · Cap-aware qty pre-fill. If earlier plans already
    // consumed part of the WO's production limit, default the qty to
    // the REMAINING balance instead of the full WO qty — otherwise the
    // "Allocate" click bounces off the server-side WO cap guard.
    let cancelled = false;
    (async () => {
      const total = Number(wo.production_qty || wo.ordered_qty || 0);
      try {
        const { data } = await api.get("/planning/allocation-balance", {
          params: { order_id: wo.source_order_id || "", item_id: resolvedItemId || "", line_id: wo.source_line_id || "" },
        });
        if (cancelled) return;
        const remaining = Number(data.remaining ?? total);
        setQty(remaining > 0 ? remaining : total);
        if (remaining <= 0 && data.already_planned > 0) {
          toast.info(`WO ${wo.wo_no || wo.po_no} is fully planned (${data.already_planned}m of ${data.cap}m). Reduce or delete an existing plan to free capacity.`);
        }
      } catch (_e) {
        if (!cancelled) setQty(total);
      }
    })();
    return () => { cancelled = true; };
  }, [wo, sourceOrder, today]);

  // Smart Search — narrow the WO picker by wo_no, product code, customer,
  // or SO/ESO number. Sorted latest-first.
  const filteredWos = useMemo(() => {
    const q = woSearch.trim().toLowerCase();
    const active = workOrders.filter(w => (w.status || "").toLowerCase() !== "cancelled");
    if (!q) return active.slice(0, 100);
    return active.filter(w =>
      (w.wo_no || w.po_no || "").toLowerCase().includes(q) ||
      (w.item_code || "").toLowerCase().includes(q) ||
      (w.source_order_no || "").toLowerCase().includes(q) ||
      (w.source_customer_name || "").toLowerCase().includes(q),
    ).slice(0, 100);
  }, [workOrders, woSearch]);

  const run = async () => {
    if (!wo) return toast.error("Pick a Work Order first");
    if (!resolvedItemId) return toast.error("This WO's article isn't in the Item Master yet — add it there first, then retry.");
    if (!qty || qty <= 0) return toast.error("Qty must be > 0");
    setLoading(true);
    try {
      const { data } = await api.post("/planning/auto-allocate", {
        order_id: wo.source_order_id || null,
        item_id: resolvedItemId,
        qty: Number(qty),
        due_date: dueDate || null,
        start_from: startFrom,
      });
      setResult(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Allocation failed"); }
    setLoading(false);
  };

  const allocate = async (rec) => {
    if (!wo) return toast.error("Pick a Work Order first");
    try {
      await api.post("/production-plans/", {
        order_id: wo.source_order_id || "", item_id: resolvedItemId,
        line_id: wo.source_line_id || "",
        machine_id: rec.machine_id, operator_id: "",
        planned_qty: Number(qty), planned_start: rec.estimated_start, planned_finish: rec.estimated_finish,
        daily_target: Math.ceil(qty / Math.max(1, rec.days_needed)),
        status: "Planned",
      });
      toast.success(`Plan created on ${rec.machine_code}`);
      onCreatePlan && onCreatePlan();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create plan"); }
  };

  return (
    <div className="space-y-4">
      <div className="tactical-card p-5">
        <div className="flex items-center gap-3 mb-4">
          <SparkleIcon size={20} className="text-[var(--accent)]" weight="bold" />
          <div className="flex-1">
            <div className="overline">Work-Order-Centric Loom Recommendation</div>
            <div className="font-display text-lg font-bold text-white">Auto Allocation Engine</div>
            <div className="text-xs text-zinc-500 mt-0.5">Pick a Work Order — every downstream field auto-fills. Scores every loom on width fit · type match · efficiency · queue load · due-date feasibility.</div>
          </div>
        </div>

        {/* Row 1 · Smart WO picker (spans the width) */}
        <div className="mb-3">
          <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Work Order</label>
          <div className="flex gap-2">
            <input
              type="search"
              data-testid="alloc-wo-search"
              value={woSearch}
              onChange={e => setWoSearch(e.target.value)}
              placeholder="Search by WO No / Product Code / SO No…"
              className={`${inp} flex-1`}
            />
            <Select value={woId} onValueChange={setWoId}>
              <SelectTrigger data-testid="alloc-wo-select" className={`${selCls} w-72`}><SelectValue placeholder={`Pick from ${filteredWos.length} WO${filteredWos.length === 1 ? "" : "s"}…`} /></SelectTrigger>
              <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-80">
                {filteredWos.map(w => (
                  <SelectItem key={w.id} value={w.id}>
                    {(w.wo_no || w.po_no)} · {w.item_code || "—"} · {w.source_order_no || w.source_type}
                  </SelectItem>
                ))}
                {filteredWos.length === 0 && <div className="px-3 py-2 text-xs text-zinc-500">No WOs match "{woSearch}"</div>}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Row 2 · Auto-hydrated read-only context labels */}
        {wo && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3 p-3 rounded-sm bg-[var(--bg-2)]/40 border border-[var(--border)]" data-testid="alloc-wo-context">
            <div><div className="overline">WO Number</div><div className="font-mono text-sm text-white">{wo.wo_no || wo.po_no}</div></div>
            <div><div className="overline">SO / ESO</div><div className="font-mono text-sm text-cyan-300">{wo.source_order_no || "—"} <span className="text-[10px] text-zinc-500">({wo.source_type || "SO"})</span></div></div>
{/* Customer hidden on production floor · Mar 2026 */}
            <div><div className="overline">Product Code</div><div className="font-mono text-xs text-emerald-300 truncate" title={wo.item_code || (linkedItem && linkedItem.code) || ""}>{wo.item_code || (linkedItem && linkedItem.code) || "—"}</div></div>
          </div>
        )}

        {/* Row 3 · Editable numeric params (pre-populated from WO) */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <F label="Qty (m)"><FormattedNumberInput data-testid="alloc-qty" value={qty} onChange={e => setQty(parseFloat(e.target.value) || 0)} className={inp} /></F>
          <F label="Start From"><input data-testid="alloc-start" type="date" value={startFrom} onChange={e => setStartFrom(e.target.value)} className={inp} /></F>
          <F label="Due Date (WO)"><input data-testid="alloc-due" type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} className={inp} /></F>
        </div>

        <div className="mt-4 flex justify-end">
          <button data-testid="alloc-run" onClick={run} disabled={loading || !wo} className="h-10 px-5 bg-[var(--accent)] hover:bg-blue-600 text-white text-sm font-semibold rounded-sm flex items-center gap-2 disabled:opacity-50">
            <SparkleIcon size={14} weight="bold" />{loading ? "Scoring…" : "Score Machines"}
          </button>
        </div>
      </div>

      {result && (
        <div className="tactical-card overflow-x-auto">
          <div className="p-4 border-b border-[var(--border)] flex items-center gap-3">
            <div className="flex-1">
              <div className="overline">Recommendations · Best First</div>
              <div className="text-xs text-zinc-500">{result.recommendations.filter(r => r.eligible).length} eligible · {result.recommendations.length - result.recommendations.filter(r => r.eligible).length} ineligible · qty {result.qty}m</div>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
                <th className="text-left px-4 py-2 font-medium">Rank</th>
                <th className="text-left px-4 py-2 font-medium">Machine</th>
                <th className="text-left px-4 py-2 font-medium">Type / Status</th>
                <th className="text-right px-4 py-2 font-medium">Score</th>
                <th className="text-right px-4 py-2 font-medium">Eff %</th>
                <th className="text-right px-4 py-2 font-medium">m/hr</th>
                <th className="text-right px-4 py-2 font-medium">Days</th>
                <th className="text-left px-4 py-2 font-medium">Start → Finish</th>
                <th className="text-center px-4 py-2 font-medium">Due?</th>
                <th className="text-left px-4 py-2 font-medium">Notes</th>
                <th className="text-right px-4 py-2 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {result.recommendations.slice(0, 15).map((r, idx) => (
                <tr key={r.machine_id} className={`border-b border-[var(--border)] ${!r.eligible ? "opacity-40" : idx === 0 ? "bg-emerald-500/[0.05]" : ""}`} data-testid={`alloc-rec-${r.machine_code}`}>
                  <td className="px-4 py-2 font-mono text-zinc-500">#{idx + 1}</td>
                  <td className="px-4 py-2 font-mono font-semibold text-white">{r.machine_code}</td>
                  <td className="px-4 py-2 text-xs text-zinc-400">{r.machine_type} · {r.status}</td>
                  <td className="px-4 py-2 text-right font-mono">
                    <span className={`font-semibold ${r.score >= 80 ? "text-emerald-400" : r.score >= 50 ? "text-amber-400" : "text-red-400"}`}>{r.score}</span>
                  </td>
                  <td className="px-4 py-2 text-right font-mono">{r.efficiency_pct}%</td>
                  <td className="px-4 py-2 text-right font-mono">{r.hourly_output_mtr}</td>
                  <td className="px-4 py-2 text-right font-mono">{r.days_needed}{r.queue_days > 0 && <span className="text-amber-400"> (+{r.queue_days})</span>}</td>
                  <td className="px-4 py-2 font-mono text-xs text-zinc-400">{r.estimated_start} → {r.estimated_finish}</td>
                  <td className="px-4 py-2 text-center">
                    {r.meets_due_date ? <CheckCircleIcon size={16} className="text-emerald-400 inline" weight="bold" /> : <WarningCircleIcon size={16} className="text-red-400 inline" weight="bold" />}
                  </td>
                  <td className="px-4 py-2 text-[10px] text-zinc-500 max-w-56">{(r.reasons || []).join(" · ") || "—"}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => allocate(r)}
                      disabled={!r.eligible || !wo}
                      data-testid={`alloc-do-${r.machine_code}`}
                      title={!wo ? "Pick a Work Order above to enable" : "Create production plan"}
                      className="h-7 px-3 rounded-sm bg-[var(--accent)] hover:bg-blue-600 text-white text-[10px] font-semibold disabled:opacity-30"
                    >
                      Allocate
                    </button>
                  </td>
                </tr>
              ))}
              {result.recommendations.length === 0 && <tr><td colSpan={11} className="text-center py-12 text-zinc-500">No machines found.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function F({ label, children, className = "" }) {
  return <div className={className}><div className="overline mb-1.5">{label}</div>{children}</div>;
}

// Feb 2026 · Live balance hint under the "Planned Qty" input in the New/Edit
// Plan dialog. Calls GET /api/planning/allocation-balance so planners see
// exactly how many meters remain against the Work Order production_qty
// (fallback: SO line Order Qty) before saving — one SO can run on multiple
// machines as long as the sum of every plan's planned_qty stays within cap.
function PlanBalanceHint({ orderId, itemId, excludePlanId = "", plannedQty = 0 }) {
  const [bal, setBal] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  React.useEffect(() => {
    let cancelled = false;
    if (!orderId || !itemId) { setBal(null); return; }
    setLoading(true);
    api.get("/planning/allocation-balance", { params: { order_id: orderId, item_id: itemId, exclude_plan_id: excludePlanId || undefined } })
      .then(r => { if (!cancelled) setBal(r.data); })
      .catch(() => { if (!cancelled) setBal(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [orderId, itemId, excludePlanId]);
  if (!orderId || !itemId) return (
    <div data-testid="plan-balance-hint-empty" className="mt-1 text-[10px] font-mono text-zinc-500">Pick an SO/ESO and an Item to see the Work Order balance.</div>
  );
  if (loading || !bal) return <div className="mt-1 text-[10px] font-mono text-zinc-500">Checking balance…</div>;
  const cap = Number(bal.cap) || 0;
  const already = Number(bal.already_planned) || 0;
  const remaining = Number(bal.remaining) || 0;
  if (cap <= 0) return (
    <div data-testid="plan-balance-hint-nocap" className="mt-1 text-[10px] font-mono text-amber-400">No Work Order or SO line found — allocation cap not enforced.</div>
  );
  const fmt = (n) => Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 });
  const willOverflow = plannedQty > 0 && (plannedQty > remaining + 0.001);
  const srcLabel = bal.source === "wo" ? "Work Order production_qty" : "SO line Order Qty";
  return (
    <div
      data-testid="plan-balance-hint"
      className={`mt-1 text-[10px] font-mono leading-tight ${willOverflow ? "text-red-400" : "text-zinc-500"}`}
      title={`Cap source: ${srcLabel}. One SO can run in parallel on multiple machines — only the SUM of planned qty must stay within this cap.`}
    >
      <span className="text-zinc-400">Cap</span> <span className="text-white">{fmt(cap)} m</span>
      <span className="text-zinc-600"> · </span>
      <span className="text-zinc-400">Already planned</span> <span className="text-white">{fmt(already)} m</span>
      <span className="text-zinc-600"> · </span>
      <span className="text-zinc-400">Remaining</span> <span className={remaining > 0 ? "text-emerald-300" : "text-red-400"}>{fmt(remaining)} m</span>
      <span className="ml-1 uppercase tracking-widest text-[9px]" data-testid="plan-balance-source">[{bal.source}]</span>
      {willOverflow && (
        <div data-testid="plan-balance-overflow" className="text-red-400 mt-0.5">
          Planned {fmt(plannedQty)} m exceeds remaining {fmt(remaining)} m — reduce or split into another plan on a different machine.
        </div>
      )}
    </div>
  );
}


// Compact stat tile used by CapacityCalendar summary strip.
function MiniStat({ label, value, unit, color = "text-white" }) {
  return (
    <div className="tactical-card p-3">
      <div className="overline text-[9px] mb-1">{label}</div>
      <div className={`font-mono text-xl font-semibold ${color}`}>{value}{unit && <span className="text-[10px] text-zinc-500 ml-1">{unit}</span>}</div>
    </div>
  );
}
